import { AlertTriangle, CheckCircle2, CircleSlash2, Inbox, LoaderCircle, RefreshCw } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import type { AdminEnvelope } from "../types";

export function PageHeader({ eyebrow, title, description, action }: { eyebrow: string; title: string; description: string; action?: React.ReactNode }) {
  return <header className="page-header"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p className="page-description">{description}</p></div>{action}</header>;
}

export function AsyncPanel<T>({ envelope, loading, error, reload, children }: { envelope: AdminEnvelope<T> | null; loading: boolean; error: string | null; reload: () => void; children: (value: AdminEnvelope<T>) => React.ReactNode }) {
  if (loading && !envelope) return <StateCard icon={<LoaderCircle className="spin" />} title="Reading authoritative state" detail="This view will update as soon as the admin API responds." />;
  if (error && !envelope) return <StateCard tone="danger" icon={<AlertTriangle />} title="This view is unavailable" detail={error} action={<button className="button secondary" onClick={reload}><RefreshCw />Try again</button>} />;
  if (!envelope) return <StateCard icon={<Inbox />} title="No response yet" detail="Refresh to request the latest operator state." action={<button className="button secondary" onClick={reload}><RefreshCw />Refresh</button>} />;
  return <>{error ? <InlineNotice tone="warning">Showing the last successful result. Refresh failed: {error}</InlineNotice> : null}{children(envelope)}</>;
}

export function StateCard({ icon, title, detail, action, tone = "neutral" }: { icon: React.ReactNode; title: string; detail: string; action?: React.ReactNode; tone?: "neutral" | "danger" }) {
  return <section className={`state-card ${tone}`} role={tone === "danger" ? "alert" : "status"}><div className="state-icon">{icon}</div><h2>{title}</h2><p>{detail}</p>{action}</section>;
}

export function InlineNotice({ children, tone = "info" }: { children: React.ReactNode; tone?: "info" | "warning" | "success" }) {
  return <div className={`notice ${tone}`} role="status">{tone === "success" ? <CheckCircle2 /> : tone === "warning" ? <AlertTriangle /> : <CircleSlash2 />}{children}</div>;
}

export function StatusBadge({ value }: { value: string | number | boolean | null | undefined }) {
  const text = value == null ? "UNKNOWN" : typeof value === "boolean" ? value ? "ENABLED" : "DISABLED" : String(value);
  const normalized = text.toUpperCase();
  const tone = /UNAVAILABLE|FAILED|NOT_PROVISIONED|DISABLED|DEAD|ERROR|^ALARM$/.test(normalized) ? "bad"
    : /PENDING|STALE|DELAY|RUNNING|INSUFFICIENT_DATA/.test(normalized) ? "warn"
    : /^(AVAILABLE|ACTIVE|OPEN|READY|ENABLED|COMPLETE|COMPLETED|SUCCEEDED|OK|FINALIZED)$/.test(normalized) ? "good" : "plain";
  return <span className={`status ${tone}`}>{text.replaceAll("_", " ")}</span>;
}

export function Metric({ label, value, detail }: { label: string; value: React.ReactNode; detail?: string }) {
  return <div className="metric"><span>{label}</span><strong>{value}</strong>{detail ? <small>{detail}</small> : null}</div>;
}

export function Section({ title, description, children, action }: { title: string; description?: string; children: React.ReactNode; action?: React.ReactNode }) {
  return <section className="panel"><header className="panel-header"><div><h2>{title}</h2>{description ? <p>{description}</p> : null}</div>{action}</header>{children}</section>;
}

export function Freshness({ asOf }: { asOf: string }) {
  return <span className="freshness">As of {new Date(asOf).toLocaleString()}</span>;
}

export function ShortId({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="muted">—</span>;
  return <code title={value}>{value.length > 18 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value}</code>;
}

export function Empty({ label }: { label: string }) { return <div className="empty"><Inbox /><p>{label}</p></div>; }

export function TypeConfirmDialog({ open, title, detail, phrase, busy, onCancel, onConfirm, children, submitLabel = "Confirm funded action" }: Readonly<{
  open: boolean; title: string; detail: string; phrase: string | null; busy: boolean;
  onCancel: () => void; onConfirm: (reason: string, confirmation: string) => void;
  submitLabel?: string; children?: React.ReactNode;
}>) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  useEffect(() => {
    const node = dialog.current;
    if (!node) return;
    if (open && !node.open) { setReason(""); setConfirmation(""); node.showModal(); }
    if (!open && node.open) node.close();
  }, [open]);
  const ready = reason.trim().length > 0 && (phrase === null || confirmation === phrase) && !busy;
  return <dialog ref={dialog} className="type-confirm" onCancel={(event) => { event.preventDefault(); if (!busy) onCancel(); }}>
    <form method="dialog" onSubmit={(event) => { event.preventDefault(); if (ready) onConfirm(reason.trim(), confirmation); }}>
      <div className="type-confirm-icon"><AlertTriangle /></div>
      <h2>{title}</h2><p>{detail}</p>{children}
      <label>Operator reason<textarea maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Reason for this change" autoFocus /></label>
      {phrase !== null ? <label>Type this exact phrase<span className="confirmation-phrase">{phrase}</span><input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" /></label> : null}
      <div className="type-confirm-actions"><button type="button" className="button secondary" disabled={busy} onClick={onCancel}>Go back</button><button className="button danger" disabled={!ready}>{busy ? "Submitting…" : submitLabel}</button></div>
    </form>
  </dialog>;
}

export function KeyValueGrid({ value }: { value: Record<string, unknown> }) {
  return <dl className="key-grid">{Object.entries(value).map(([key, item]) => <div key={key}><dt>{humanize(key)}</dt><dd>{renderValue(item)}</dd></div>)}</dl>;
}

export function humanize(value: string): string { return value.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replaceAll("_", " ").replace(/^./, (char) => char.toUpperCase()); }
function renderValue(value: unknown): React.ReactNode {
  if (typeof value === "boolean" || typeof value === "string" || typeof value === "number" || value == null) return <StatusBadge value={value as string | number | boolean | null} />;
  if (Array.isArray(value)) return value.length ? `${value.length} records` : "None";
  return <code>{JSON.stringify(value)}</code>;
}
